// /agents — the project's AGENTS, first-class cortex entities, presented with
// the SAME Drive-style idiom as the Apps page: a left rail of agent rows
// ("My agents" / "Shared with me" / "Organization"), hover actions, a per-row
// ⋯ menu, and a header "Request an agent" button that opens the shared
// AppRequestDialog machinery (POST /api/app_requests). Clicking an agent row is
// the primary action — it opens the AGENT PAGE (AgentPage.jsx, /agents/:id):
// the agent's configuration with a "Chat with agent" button. The row's "Ask"
// action opens the project's orchestrator CHAT switched to that agent (a
// pinned-agent chip so the chat routes to it), via the `cortex:open-harness`
// CustomEvent carrying a `selectedAgent` { name, display_name, icon }.
//
// Each agent has an owner, display name, description, icon, a visibility model,
// and chips for the apps + data sources it operates over. Owners/admins
// (can_manage) get rename-less manage affordances here: visibility toggle,
// share picker (AgentShareDialog), Request a change, and Remove.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot, Loader2, RefreshCw, AppWindow, Box, Globe, Lock, Users, Share2,
  MessageSquare, MoreVertical, Wrench, Trash2, Sparkles, PanelLeftClose,
  PanelLeftOpen, ClipboardList, History,
} from "lucide-react";
import {
  listAgents, updateAgentVisibility, shareAgent, unshareAgent, removeAgent,
  listOrgMembers, listAppRequests,
} from "../services/apps";
import { createPageUrl } from "@/utils";
import { useProject } from "@/contexts/ProjectContext";
import AppRequestDialog from "../components/AppRequestDialog";
import AgentIcon, { emojiOf } from "../components/AgentIcon";
import VersionHistory from "../components/VersionHistory";
import { isPortal } from "../utils/portal";

const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30";
const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";

// Kind metadata for the shared AppRequestDialog on the Agents page. new_agent
// takes a title + details brief (no ids); change_agent targets an existing
// agent (needsTarget → agent_id) — the backend contract for /api/app_requests.
const AGENT_REQUEST_KINDS = [
  { kind: "new_agent", label: "New agent", icon: Sparkles,
    hint: "Describe the agent you need — an agent will build it and you'll own it.",
    titlePlaceholder: "What should the agent be called / do?",
    detailsPlaceholder: "Describe the agent in plain language — what it should do, the data/apps it should work over, who it's for…" },
  { kind: "change_agent", label: "Change agent", icon: Wrench,
    hint: "Build a change into an existing agent.",
    needsTarget: true, targetLabel: "Select an agent…", titlePlaceholder: "Short summary",
    detailsPlaceholder: "Describe what should change about this agent…" },
];

export default function Agents() {
  const navigate = useNavigate();
  const { projectSlug, branch, sharedSurfaceOnly } = useProject();
  // Someone who reaches this project only through a shared app/agent cannot
  // build anything in it (the request would 403), so don't offer to.
  const canBuild = !isPortal() && !sharedSurfaceOnly;

  const [agents, setAgents] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [shareTarget, setShareTarget] = useState(null); // agent being shared
  const [historyTarget, setHistoryTarget] = useState(null); // agent — version history
  const [removeTarget, setRemoveTarget] = useState(null); // agent being removed
  const [busy, setBusy] = useState(false);
  const [dialogErr, setDialogErr] = useState(null);
  // ⋯ menu open state (per agent id).
  const [menuAgentId, setMenuAgentId] = useState(null);
  // Request dialog: { kind, agent? } — shared AppRequestDialog machinery.
  const [requestDialog, setRequestDialog] = useState(null);
  // Open (queued/claimed/running) request count for the rail badge.
  const [openRequests, setOpenRequests] = useState(0);

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("agentsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("agentsRailOpen", n ? "1" : "0"); } catch {} return n; });

  const load = () => {
    setLoading(true);
    setErr(null);
    listAgents()
      .then((r) => { setAgents(r?.agents || []); setMe(r?.me || null); })
      .catch((e) => {
        // Don't show the raw "No access to project '<slug>'" from the access
        // gate — for someone who only holds a shared app that reads as a
        // failure when it simply means no agent was shared with them.
        const msg = e?.message || String(e);
        setErr(/no access to project/i.test(msg)
          ? "No agents in this project have been shared with you."
          : msg);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let alive = true;
    listAppRequests({ status: "open" })
      .then((r) => { if (alive) setOpenRequests((r?.requests || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [requestDialog]);

  // Close the row menu on outside click (matches Apps).
  useEffect(() => {
    if (menuAgentId == null) return;
    const close = () => setMenuAgentId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuAgentId]);

  const displayName = (h) => h.display_name || h.name;

  // ROW CLICK — the primary action opens the AGENT PAGE (/agents/:id): the
  // agent's configuration — persona, model, tools, what it works over, how it
  // runs, access — with "Chat with agent" on it. Until 2026-09-12 the click
  // opened the chat; the configuration was only readable in the Flow studio
  // (Alex, before the Allvue CTO demo: "not low level like this").
  const openAgent = (h) => navigate(createPageUrl(`agents/${h.id}`));
  // "Ask" opens the project's orchestrator chat SWITCHED to this agent. The
  // chat is mounted globally (Layout) and binds to the active project from the
  // URL; we fire the same `cortex:open-harness` event Apps.jsx uses, carrying
  // `selectedAgent` so AssistantChat threads it through chat-config → ws →
  // prompt injection and chat.html pins a chip.
  const askAgent = (h) => {
    window.dispatchEvent(new CustomEvent("cortex:open-harness", {
      detail: {
        harness: "orchestrator",
        // The chat chip can only show TEXT: an emoji icon as-is, a lucide
        // name (fleet-built agents) as the robot.
        selectedAgent: { name: h.name, display_name: displayName(h), icon: emojiOf(h.icon) },
      },
    }));
  };

  const openApp = (viewName) => navigate(createPageUrl(`apps/${encodeURIComponent(viewName)}`));
  // Sources are project-scoped; the source detail page is data-sources/:id.
  const openSource = (src) => navigate(createPageUrl(`data-sources/${encodeURIComponent(src.source_id)}`));

  const patchLocal = (id, patch) => setAgents((prev) => prev.map((h) => (h.id === id ? { ...h, ...patch } : h)));

  const doRemove = async (h) => {
    setBusy(true); setDialogErr(null);
    try {
      await removeAgent(h.id);
      setAgents((prev) => prev.filter((a) => a.id !== h.id));
      setRemoveTarget(null);
    } catch (e) { setDialogErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  // ── Rail sections (mirror Apps: My / Shared / Organization) ────────────────
  const myAgents = agents.filter((h) => h.is_owner);
  const sharedAgents = agents.filter((h) => !h.is_owner && h.shared_with_me);
  const orgAgents = agents.filter((h) => !h.is_owner && !h.shared_with_me);

  const AgentRow = ({ h }) => {
    const menuOpen = menuAgentId === h.id;
    const canManage = h.can_manage || h.is_owner;
    const hasChips = (h.apps?.length > 0 || h.sources?.length > 0);
    return (
      <div
        data-testid={`agent-${h.id}`}
        className="group relative w-full rounded-md text-sm text-page-text hover:bg-card-border/40"
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`agent-open-${h.id}`}
            onClick={() => openAgent(h)}
            className="flex-1 min-w-0 text-left px-3 py-2 flex items-start gap-2 cursor-pointer border-0 bg-transparent text-inherit"
          >
            {/* A lucide NAME in `icon` (fleet-built agents) is drawn as the
                icon, never as a word that overflows the 16-px slot. */}
            <AgentIcon icon={h.icon} size={16} className="mt-0.5 text-page-muted" />
            <span className="min-w-0 flex-1 flex flex-col">
              <span className="flex items-start gap-1.5 min-w-0">
                <span className="min-w-0 break-words leading-snug">{displayName(h)}</span>
                {(h.visibility === "org")
                  ? <Globe size={12} className="shrink-0 opacity-60 mt-1" title="Visible to everyone in the organization" />
                  : (h.shares?.length
                    ? <Users size={12} className="shrink-0 opacity-60 mt-1" title={`Shared with ${h.shares.length}`} />
                    : <Lock size={12} className="shrink-0 opacity-60 mt-1" title="Private" />)}
              </span>
              {h.description && (
                // Two lines, then an ellipsis — wraps instead of running off the rail.
                <span className="text-xs text-page-muted break-words line-clamp-2 leading-snug" title={h.description}>{h.description}</span>
              )}
            </span>
          </button>
          <button
            type="button"
            data-testid={`agent-ask-${h.id}`}
            title="Ask this agent"
            onClick={(e) => { e.stopPropagation(); askAgent(h); }}
            className="shrink-0 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-nav-active opacity-0 group-hover:opacity-100"
          >
            <MessageSquare size={14} />
          </button>
          {canBuild && (<button
            type="button"
            data-testid={`agent-menu-${h.id}`}
            title="More"
            onClick={(e) => { e.stopPropagation(); setMenuAgentId(menuOpen ? null : h.id); }}
            className={
              "shrink-0 mr-1 p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-page-text opacity-0 group-hover:opacity-100 " +
              (menuOpen ? "opacity-100" : "")
            }
          >
            <MoreVertical size={14} />
          </button>)}
        </div>
        {/* Relation chips — the apps + data sources this agent operates over.
            Click navigates (like the Apps page opening an app / a source). */}
        {hasChips && (
          <div className="flex flex-wrap gap-1.5 px-3 pb-2 pl-[38px]">
            {(h.apps || []).map((a) => (
              <button
                key={`app-${a.id}`}
                type="button"
                data-testid={`agent-app-${a.view_name}`}
                title="Open app"
                onClick={(e) => { e.stopPropagation(); openApp(a.view_name); }}
                className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-0.5 cursor-pointer hover:bg-card-border/30"
              >
                <AppWindow size={11} className="opacity-70" />
                <span className="max-w-[130px] truncate">{a.title || a.view_name}</span>
              </button>
            ))}
            {(h.sources || []).map((s) => (
              <button
                key={`src-${s.source_id}`}
                type="button"
                data-testid={`agent-source-${s.source_id}`}
                title="Open data source"
                onClick={(e) => { e.stopPropagation(); openSource(s); }}
                className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-0.5 cursor-pointer hover:bg-card-border/30"
              >
                <Box size={11} className="opacity-70" />
                <span className="max-w-[130px] truncate">{s.source_id}</span>
              </button>
            ))}
          </div>
        )}
        {menuOpen && (
          <div
            className="absolute right-1 top-full z-40 mt-1 w-52 rounded-md border border-card-border bg-card-bg shadow-lg py-1 text-page-text"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem icon={MessageSquare} label="Ask" onClick={() => { setMenuAgentId(null); askAgent(h); }} />
            <MenuItem icon={Share2} label="Share…" onClick={() => { setMenuAgentId(null); setShareTarget(h); }} />
            {canManage && (
              <MenuItem
                icon={h.visibility === "org" ? Globe : Lock}
                label={h.visibility === "org" ? "Make private" : "Make org-visible"}
                onClick={async () => {
                  setMenuAgentId(null);
                  const next = h.visibility === "org" ? "private" : "org";
                  try { await updateAgentVisibility(h.id, next); patchLocal(h.id, { visibility: next }); }
                  catch (e) { setErr(e?.message || String(e)); }
                }}
              />
            )}
            <MenuItem icon={Wrench} label="Build a change" onClick={() => {
              setMenuAgentId(null);
              setRequestDialog({ kind: "change_agent", agent: h });
            }} />
            {/* Same reasoning as the Apps row menu: history must be reachable
                from the LIST, without depending on the agent working. */}
            <MenuItem icon={History} label="Version history" onClick={() => { setMenuAgentId(null); setHistoryTarget(h); }} />
            {canManage && (
              <MenuItem icon={Trash2} label="Remove agent" danger onClick={() => { setMenuAgentId(null); setDialogErr(null); setRemoveTarget(h); }} />
            )}
          </div>
        )}
      </div>
    );
  };

  const Section = ({ label, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex h-full min-h-0">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="agents-rail-open"
            onClick={toggleRail}
            title="Show agents list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftOpen size={18} />
          </button>
          <Bot size={16} className="text-nav-active mt-1" />
        </div>
      )}
      {/* Left rail: the Drive-like agents explorer (collapsible), mirroring Apps */}
      <aside className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <Bot size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Agents</span>
          {canBuild && (<button
            type="button"
            data-testid="agents-requests-badge"
            title="Open builds"
            className="ml-auto relative text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
            onClick={() => setRequestDialog({ kind: "new_agent" })}
          >
            <ClipboardList size={15} />
            {openRequests > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-nav-active text-white text-[9px] leading-[14px] text-center">
                {openRequests}
              </span>
            )}
          </button>)}
          <button
            type="button"
            data-testid="agents-refresh"
            onClick={load}
            title="Refresh"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <RefreshCw size={14} />
          </button>
          <button
            type="button"
            data-testid="agents-rail-close"
            onClick={toggleRail}
            title="Collapse agents list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading agents…
            </div>
          )}
          {!loading && err && <div className="text-sm text-red-500 px-2 py-3">{err}</div>}
          {!loading && !err && agents.length === 0 && (
            <div className="text-sm text-page-muted px-2 py-3">
              No agents yet in this project. Request one below.
            </div>
          )}
          {!loading && !err && myAgents.length > 0 && (
            <Section label="My agents">
              {myAgents.map((h) => <AgentRow key={h.id} h={h} />)}
            </Section>
          )}
          {!loading && !err && sharedAgents.length > 0 && (
            <Section label="Shared with me">
              {sharedAgents.map((h) => <AgentRow key={h.id} h={h} />)}
            </Section>
          )}
          {!loading && !err && orgAgents.length > 0 && (
            <Section label="Organization">
              {orgAgents.map((h) => <AgentRow key={h.id} h={h} />)}
            </Section>
          )}
        </div>
        {/* Rail footer: file a request for a new agent (agent-built). */}
        <div className="p-2 border-t border-card-border">
          {canBuild && (<button
            type="button"
            data-testid="agents-request-new"
            onClick={() => setRequestDialog({ kind: "new_agent" })}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
          >
            <Sparkles size={14} /> Build an agent
          </button>)}
        </div>
      </aside>

      {/* Main: welcome / empty state (clicking an agent opens the CHAT, not this pane) */}
      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <Bot size={40} className="text-page-muted mb-3" />
          <div className="text-page-text font-medium">Your project agents</div>
          <div className="text-sm text-page-muted mt-1 max-w-md">
            Pick an agent from the left to open the chat switched to it — it will route
            your messages to that agent. Each agent operates over the apps and data
            sources linked to it.
          </div>
          {canBuild && (<button
            type="button"
            data-testid="agents-request-new-main"
            onClick={() => setRequestDialog({ kind: "new_agent" })}
            className={btnGhost + " mt-4"}
          >
            <Sparkles size={14} /> Build an agent
          </button>)}
        </div>
      </main>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      {removeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRemoveTarget(null)} />
          <div className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5" style={{ width: 440, maxWidth: "calc(100vw - 32px)" }}>
            <div className="font-semibold text-page-text mb-4">Remove agent</div>
            <div className="text-sm text-page-text mb-4">
              Remove <b>{displayName(removeTarget)}</b>? People it was shared with will lose access,
              and its endpoints on the paired fork are deleted. This does not touch the data or
              apps it operated over.
            </div>
            {dialogErr && <div className="text-sm text-red-500 mb-3">{dialogErr}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setRemoveTarget(null)}>Cancel</button>
              <button type="button" data-testid="agent-remove-confirm" className={btnPrimary + " bg-red-600"} disabled={busy} onClick={() => doRemove(removeTarget)}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {requestDialog && (
        <AppRequestDialog
          kinds={AGENT_REQUEST_KINDS}
          targets={agents.map((h) => ({ id: h.id, label: displayName(h) }))}
          targetField="agent_id"
          title="Build an agent"
          preset={{
            kind: requestDialog.kind,
            title: requestDialog.agent ? `Change agent ${displayName(requestDialog.agent)}` : "",
            targetId: requestDialog.agent?.id ?? null,
          }}
          onClose={() => setRequestDialog(null)}
          onCreated={() => setRequestDialog(null)}
        />
      )}

      {historyTarget && (
        <AgentHistoryDialog agent={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}

      {shareTarget && (
        <AgentShareDialog
          agent={agents.find((h) => h.id === shareTarget.id) || shareTarget}
          me={me}
          onClose={() => setShareTarget(null)}
          onVisibility={async (v) => { await updateAgentVisibility(shareTarget.id, v); patchLocal(shareTarget.id, { visibility: v }); }}
          onShare={async (email) => {
            const r = await shareAgent(shareTarget.id, email);
            patchLocal(shareTarget.id, {
              shares: [...(agents.find((h) => h.id === shareTarget.id)?.shares || []), { user_id: r.user_id, email: r.email, name: null }],
            });
          }}
          onUnshare={async (userId) => {
            await unshareAgent(shareTarget.id, userId);
            patchLocal(shareTarget.id, {
              shares: (agents.find((h) => h.id === shareTarget.id)?.shares || []).filter((s) => s.user_id !== userId),
            });
          }}
          openApp={openApp}
          openSource={openSource}
        />
      )}
    </div>
  );
}

// An agent IS a `cortex.agent.<name>.manifest` endpoint on the fork, so Flow's
// git already versions it. Its own dialog (rather than a section inside Share…)
// so history is reachable from the row menu without opening anything else.
function AgentHistoryDialog({ agent, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* Header pinned, body scrolls — see the Apps Modal for the same reasoning. */}
      <div
        className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5 flex flex-col"
        style={{ width: 560, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh" }}
      >
        <div className="flex items-start justify-between mb-4 shrink-0">
          <div className="min-w-0">
            <div className="text-base font-semibold text-page-text truncate">Version history</div>
            <div className="text-xs text-page-muted truncate">{agent.display_name || agent.name}</div>
          </div>
          <button type="button" onClick={onClose} title="Close"
                  className="p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-page-text shrink-0">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto min-h-0">
          <VersionHistory kind="agent" name={agent.name}
                          canRestore={agent.can_manage !== false} />
        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 cursor-pointer border-0 bg-transparent " +
        (danger ? "text-red-500 hover:bg-red-500/10" : "text-page-text hover:bg-card-border/40")
      }
    >
      <Icon size={14} className="shrink-0 opacity-80" /> {label}
    </button>
  );
}

// Share dialog for an agent. The Apps page's ShareDialog is defined privately
// inside Apps.jsx (not exported), so it is not cleanly reusable without
// exporting it. Rather than restructure a sibling-owned export this round, this
// is a focused copy of the same pattern (link-less: agents have no standalone
// share URL) — visibility choices + an org-member people picker — plus the
// linked apps/sources chips so the share surface doubles as the agent detail.
import { X, Check, UserRound } from "lucide-react";

const inputCls = "w-full rounded-md border border-card-border bg-page-bg px-3 py-2 text-sm text-page-text outline-none focus:border-nav-active";

function AgentShareDialog({ agent, me, onClose, onVisibility, onShare, onUnshare, openApp, openSource }) {
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    listOrgMembers().then((r) => setMembers(r?.members || [])).catch(() => {});
  }, []);

  const run = async (fn) => {
    setPending(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e?.message || String(e)); } finally { setPending(false); }
  };

  const candidates = members.filter(
    (m) => m.id !== me?.id && !(agent.shares || []).some((s) => s.user_id === m.id)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5"
        style={{ width: 480, maxWidth: "calc(100vw - 32px)", maxHeight: "80vh", overflowY: "auto" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-page-text flex items-center gap-2">
            <span aria-hidden>{agent.icon || "🤖"}</span>
            Share “{agent.display_name || agent.name}”
          </div>
          <button type="button" onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <X size={16} />
          </button>
        </div>

        {/* Linked apps + sources */}
        {(agent.apps?.length > 0 || agent.sources?.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {(agent.apps || []).map((a) => (
              <button
                key={`app-${a.id}`}
                type="button"
                data-testid={`agent-app-${a.view_name}`}
                title="Open app"
                onClick={() => openApp(a.view_name)}
                className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-1 cursor-pointer hover:bg-card-border/30"
              >
                <AppWindow size={12} className="opacity-70" />
                <span className="max-w-[160px] truncate">{a.title || a.view_name}</span>
              </button>
            ))}
            {(agent.sources || []).map((s) => (
              <button
                key={`src-${s.source_id}`}
                type="button"
                data-testid={`agent-source-${s.source_id}`}
                title="Open data source"
                onClick={() => openSource(s)}
                className="inline-flex items-center gap-1 rounded-full border border-card-border bg-transparent text-xs text-page-text px-2 py-1 cursor-pointer hover:bg-card-border/30"
              >
                <Box size={12} className="opacity-70" />
                <span className="max-w-[160px] truncate">{s.source_id}</span>
              </button>
            ))}
          </div>
        )}

        {/* Visibility */}
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-page-muted mb-2">General access</div>
          <div className="space-y-1.5">
            <VisibilityChoice
              icon={Lock}
              label="Private"
              hint="Only you and people you share with can use it"
              active={agent.visibility === "private"}
              disabled={pending}
              onPick={() => run(() => onVisibility("private"))}
            />
            <VisibilityChoice
              icon={Globe}
              label="Anyone in the organization"
              hint="Every member of your organization can use it"
              active={agent.visibility === "org"}
              disabled={pending}
              onPick={() => run(() => onVisibility("org"))}
            />
          </div>
        </div>

        {/* People */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-page-muted mb-2">People with access</div>
          <div className="flex items-center gap-2 mb-2">
            <input
              data-testid="agent-share-email-input"
              className={inputCls + " flex-1"}
              placeholder="Add a member by email…"
              value={email}
              list="agent-share-members"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) run(async () => { await onShare(email.trim()); setEmail(""); }); }}
            />
            <datalist id="agent-share-members">
              {candidates.map((m) => <option key={m.id} value={m.email}>{m.name || m.email}</option>)}
            </datalist>
            <button
              type="button"
              data-testid="agent-share-add"
              className={btnPrimary + " shrink-0"}
              disabled={pending || !email.trim()}
              onClick={() => run(async () => { await onShare(email.trim()); setEmail(""); })}
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Add
            </button>
          </div>
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {agent.owner && (
              <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-page-text">
                <UserRound size={14} className="opacity-70" />
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{agent.owner.name || agent.owner.email || "—"}</span>
                <span className="ml-auto text-xs text-page-muted">Owner</span>
              </div>
            )}
            {(agent.shares || []).map((s) => (
              <div key={s.user_id} className="flex items-center gap-2 px-2 py-1.5 text-sm text-page-text rounded-md hover:bg-card-border/30">
                <UserRound size={14} className="opacity-70" />
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{s.name || s.email}</span>
                <button
                  type="button"
                  data-testid={`agent-unshare-${s.user_id}`}
                  title="Remove access"
                  disabled={pending}
                  onClick={() => run(() => onUnshare(s.user_id))}
                  className="ml-auto p-1 rounded cursor-pointer border-0 bg-transparent text-page-muted hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {(agent.shares || []).length === 0 && (
              <div className="text-sm text-page-muted px-2 py-1.5">Not shared with anyone yet.</div>
            )}
          </div>
        </div>
        {err && <div className="text-sm text-red-500 mt-3">{err}</div>}
      </div>
    </div>
  );
}

function VisibilityChoice({ icon: Icon, label, hint, active, disabled, onPick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPick}
      className={
        "w-full text-left px-3 py-2 rounded-md flex items-start gap-2.5 cursor-pointer border " +
        (active ? "border-nav-active bg-nav-active/10" : "border-card-border bg-transparent hover:bg-card-border/30")
      }
    >
      <Icon size={15} className={"mt-0.5 shrink-0 " + (active ? "text-nav-active" : "text-page-muted")} />
      <span className="min-w-0">
        <span className={"block text-sm " + (active ? "text-nav-active font-medium" : "text-page-text")}>{label}</span>
        <span className="block text-xs text-page-muted">{hint}</span>
      </span>
      {active && <Check size={15} className="ml-auto mt-0.5 text-nav-active" />}
    </button>
  );
}
